import {
    angleDegAtVertex,
    euclideanDistanceMm,
    PointMm,
} from '~/domain/geometry/patientGeometry';
import { Measurement } from './measurement';

/**
 * `measurement` with the point at `pointIndex` replaced and its cached value
 * recomputed. Patient-space mm in and out; conversion stays at the display
 * boundary.
 *
 * @returns the unchanged input for an out-of-range index, and null when the
 *   edit would make an angle degenerate, which the caller rejects rather than
 *   store an invalid clinical value.
 */
export function updateMeasurementPoint(
    measurement: Measurement,
    pointIndex: number,
    newPoint: PointMm
): Measurement | null {
    // Defensive: never grow or shift the fixed point tuple. The valid bound
    // is intrinsic to the kind (2 points for distance, 3 for an angle).
    if (pointIndex < 0 || pointIndex >= measurement.points.length) {
        return measurement;
    }
    if (measurement.kind === 'distance') {
        const points: readonly [PointMm, PointMm] =
            pointIndex === 0
                ? [newPoint, measurement.points[1]]
                : [measurement.points[0], newPoint];
        return {
            ...measurement,
            points,
            distanceMm: euclideanDistanceMm(points[0], points[1]),
        };
    }

    const points: [PointMm, PointMm, PointMm] = [
        measurement.points[0],
        measurement.points[1],
        measurement.points[2],
    ];
    points[pointIndex] = newPoint;
    const angleDeg = angleDegAtVertex(points[0], points[1], points[2]);
    if (!Number.isFinite(angleDeg)) {
        return null;
    }
    return { ...measurement, points, angleDeg };
}
